import { selectInputsByIds } from "@/features/inputs/api/selectInputsByIds";
import type { InputT } from "@patient-watch/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { validate as validateUUID } from "uuid";

interface InputWaiter {
    inputId: string;
    resolve: (input: InputT) => void;
    reject: (error: unknown) => void;
}

const inputWaiters: InputWaiter[] = [];
let inputFlushTimer: ReturnType<typeof setTimeout> | null = null;

const INPUT_LOOKUP_BATCH = 40;

function flushInputLookups() {
    inputFlushTimer = null;
    const batch = inputWaiters.splice(0, inputWaiters.length);
    const ids = [...new Set(batch.map((waiter) => waiter.inputId))].slice(0, INPUT_LOOKUP_BATCH);
    const current = batch.filter((waiter) => ids.includes(waiter.inputId));
    const overflow = batch.filter((waiter) => !ids.includes(waiter.inputId));
    if (overflow.length > 0) {
        inputWaiters.unshift(...overflow);
        inputFlushTimer = setTimeout(flushInputLookups, 0);
    }
    void selectInputsByIds(ids)
        .then((rows) => {
            const byId = new Map(rows.map((row) => [row.input_id, row]));
            for (const waiter of current) {
                const row = byId.get(waiter.inputId);
                if (row) waiter.resolve(row);
                else waiter.reject(new Error("Input not found"));
            }
        })
        .catch((error: unknown) => {
            for (const waiter of current) waiter.reject(error);
        });
}

/** Coalesce same-tick lookups into one `inputs?input_id=in.(...)` request (WEBAPP-AD). */
export function selectInputById(inputId: string): Promise<InputT> {
    if (!validateUUID(inputId)) {
        return Promise.reject(new Error("Input id is not a uuid"));
    }

    return new Promise((resolve, reject) => {
        inputWaiters.push({ inputId, resolve, reject });
        if (inputFlushTimer != null) return;
        inputFlushTimer = setTimeout(flushInputLookups, 0);
    });
}

export const selectInputByIdQueryOptions = (inputId: string) =>
    queryOptions({
        queryKey: ["input", { input_id: inputId }],
        queryFn: async () => selectInputById(inputId),
        enabled: Boolean(inputId) && validateUUID(inputId),
    });

export function useSelectInputById(inputId: string) {
    const options = selectInputByIdQueryOptions(inputId);
    return useQuery(options);
}
